import 'braid-design-system/reset';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router';

import { App } from './App/App.tsx';
import { ColorModeProvider } from './components/ColorModeProvider/ColorModeProvider.tsx';
import { DatadogProvider } from './components/Datadog/Datadog.tsx';
import { ScrollRestoration } from './components/ScrollRestoration/ScrollRestoration.tsx';
import { AuthProvider } from './components/auth/AuthProvider.tsx';
import { ApolloEnvironmentProvider } from './hooks/apollo.tsx';
import { EnvironmentProvider } from './hooks/environment.tsx';
import { StaticRenderProvider } from './hooks/staticRender.tsx';
import type { ClientContext } from './types';
import { getTealiumSrcUrl } from './utils/analyticsClient.ts';

import build from '#src/build.json' with { type: 'json' };

export default function render({
  environment,
  ssr,
  version: _version,
}: ClientContext) {
  const tealiumUrl = getTealiumSrcUrl(environment);

  document.addEventListener('DOMContentLoaded', () => {
    if (tealiumUrl) {
      const scriptElement = document.createElement('script');
      scriptElement.src = tealiumUrl;
      scriptElement.async = true;
      document.head.appendChild(scriptElement);
    }
  });

  const Application = () => (
    <EnvironmentProvider value={environment}>
      <DatadogProvider
        env={environment}
        version={`${build.branch}-${build.number}`}
      >
        <StaticRenderProvider value={false}>
          <ColorModeProvider>
            <AuthProvider>
              <ApolloEnvironmentProvider>
                <App />
              </ApolloEnvironmentProvider>
            </AuthProvider>
            <ScrollRestoration storageKey="developer-site-scroll-positions" />
          </ColorModeProvider>
        </StaticRenderProvider>
      </DatadogProvider>
    </EnvironmentProvider>
  );

  const router = createBrowserRouter([
    {
      path: '*',
      element: <Application />,
    },
  ]);

  if (ssr) {
    hydrateRoot(
      document.getElementById('root')!,
      <RouterProvider router={router} />,
    );
  } else {
    const root = createRoot(document.getElementById('root')!);
    root.render(<RouterProvider router={router} />);
  }
}
